import { contractMobileProfile, expandMobileProfile } from './animations'

/**
 * Mobile UI for profile list/bios. Behaviour:
 * - List items include thumbnail, name and role.
 * - Profile includes full image, close button, name, role, LinkedIn icon/link and bio.
 * - Click to expand full profile (different element which replaces list item when open).
 * - Profile closes on close button click or when another profile opened.
 *
 * See Figma file for intended layout/behaviour:
 * https://www.figma.com/file/YQ73BIauH99X8ksuCY1hcZ/Marketing---website-content-blueprint?type=design&node-id=841-4886&mode=dev
 */
const MobileProfile = (function () {
  let listItemEls = {}

  /**
   * Initialise any event handlers required to interact with people list items/profiles
   */
  const initEventHandlers = () => {
    listItemEls.forEach((item) => {
      const itemTile = item.querySelector('.js-mobile-profile-tile')
      if (itemTile) {
        itemTile.addEventListener('click', handleItemClick)

        // Anchor element inside tile div (click triggered clicking anywhere in tile)
        const anchorLinkEl = item.querySelector('.js-profile-link')
        if (anchorLinkEl) {
          anchorLinkEl.addEventListener('click', handleProfileAnchorClick)
        }

        // Close button
        const closeBtnEl = item.querySelector('.js-close-profile-btn')
        if (closeBtnEl) {
          closeBtnEl.addEventListener('click', handleCloseBtnClick)
        }
      }
    })
  }

  /**
   * Remove any event handlers required to interact with people list/profiles
   */
  const killEventHandlers = () => {
    listItemEls.forEach((item) => {
      item.removeEventListener('click', handleItemClick)

      const anchorLinkEl = item.querySelector('.js-profile-link')
      if (anchorLinkEl) {
        anchorLinkEl.removeEventListener('click', handleProfileAnchorClick)
      }

      const closeBtnEl = item.querySelector('.js-close-profile-btn')
      if (closeBtnEl) {
        closeBtnEl.removeEventListener('click', handleCloseBtnClick)
      }
    })
  }

  /**
   * Handle click within list item tile. Triggers click on internal anchor link.
   * @param {Event} e
   */
  const handleItemClick = (e) => {
    // Don't trigger click if click was already on anchor element
    if (!e.target.classList.contains('js-profile-link')) {
      const profileTile = e.target.closest('.js-mobile-profile-tile')
      if (profileTile) {
        const itemAnchorLinkEl = profileTile.querySelector('.js-profile-link')
        if (itemAnchorLinkEl) {
          itemAnchorLinkEl.click()
        }
      }
    }
  }

  /**
   * Handle click on anchor link element (person's name) within list item tile
   * @param {Event} e
   */
  const handleProfileAnchorClick = (e) => {
    e.preventDefault()
    // Allow user to open link in new tab if ctrl/cmd button pressed
    if (e.ctrlKey || e.metaKey) {
      window.open(e.target.getAttribute('href'), '_blank')
    } else {
      openProfile(e.target.closest('.js-mobile-profile-item'))
    }
  }

  /**
   * Close profile (parent of clicked button) and revert to list item layout.
   * @param {Event} e
   */
  const handleCloseBtnClick = (e) => {
    e.stopPropagation()
    const profile = e.target.closest('.js-mobile-profile-item')
    if (profile) {
      closeProfile(profile)
    }
  }

  /**
   * Given profile item (parent li of both list tile and profile elements), expand profile and hide list tile.
   * Also handles focus managment and URL updates
   * @param {Element} profileItem
   */
  const openProfile = (profileItem) => {
    if (profileItem) {
      // Close any other open profile
      const openItem = document.querySelector('.c-profile-list__item--active')
      closeProfile(openItem)

      // Transition from list item to profile view
      expandMobileProfile(profileItem)

      // Set aria-expanded property on profile link
      const profileOpenLink = profileItem.querySelector('.js-profile-link')
      if (profileOpenLink) {
        profileOpenLink.setAttribute('aria-expanded', 'true')
      }

      // Focus management (for better a11y)
      const closeBtn = profileItem.querySelector('.js-close-profile-btn')
      if (closeBtn) {
        closeBtn.focus()
        closeBtn.setAttribute('aria-expanded', 'true')
      }

      // Update URL to match currently open profile
      const listTile = profileItem.querySelector('.js-mobile-profile-tile')
      if (listTile) {
        const hashLink = listTile.dataset.person
        history.replaceState('', document.title, window.location.pathname + window.location.search + `#${hashLink}`)
      }
    }
  }

  /**
   * Given profile item (parent li of both list tile and profile elements), contract profile and replace with list tile.
   * Also handles focus managment and URL updates
   * @param {Element} profileItem
   */
  const closeProfile = (profileItem) => {
    if (profileItem) {
      contractMobileProfile(profileItem)

      // Focus management (for better a11y)
      const profileOpenLink = profileItem.querySelector('.js-profile-link')
      if (profileOpenLink) {
        profileOpenLink.focus()
        profileOpenLink.setAttribute('aria-expanded', 'false')
      }

      // Set aria-expanded property on close button
      const closeBtn = profileItem.querySelector('.js-close-profile-btn')
      if (closeBtn) {
        closeBtn.setAttribute('aria-expanded', 'false')
      }

      // Update URL to remove anchor for current profile
      history.replaceState('', document.title, window.location.pathname + window.location.search)
    }
  }

  return {
    /**
     * Initialise mobile UI. Opens active profile on load if valid identifier provided (kebab cased first/last names).
     * @param {string} activeProfile
     */
    init: (activeProfile) => {
      // Query all list item elements on the page
      listItemEls = document.querySelectorAll('.js-mobile-profile-item')

      initEventHandlers()

      // If active profile identifier provided, query element and open if valid
      if (activeProfile) {
        const profileEl = document.querySelector(`.js-mobile-profile-tile[data-person="${activeProfile}"]`)
        if (profileEl) {
          const parentItem = profileEl.closest('.js-mobile-profile-item')
          if (parentItem) {
            openProfile(parentItem)
          }
        }
      }

      // Update aria attributes to match mobile behaviour (SS templates match desktop by default)
      document.querySelectorAll('.js-profile-link').forEach((link) => {
        link.setAttribute('aria-controls', link.getAttribute('aria-controls') + '-mobile')
      })
    },
    kill: () => {
      killEventHandlers()
    },
  }
})()

export default MobileProfile
